import {
  createSlice,
  createAction,
  appLogout,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import { GeoFenceState } from './geoFence.types';

// Matched by type instead of importing the thunks: importing
// @bonanza-social/auth/state here creates a circular import through
// transmit-resolver that leaves them undefined at load time
const SESSION_END_ACTION_TYPES = [
  appLogout.type,
  'auth/logout/rejected',
  'auth/refreshToken/rejected',
];

export const loginPingStarted = createAction('geoFence/loginPingStarted');
export const locationSheetAnswered = createAction<
  'granted' | 'denied' | 'later'
>('geoFence/locationSheetAnswered');
// A ping reached Kade, so the backend will decide via the webhook and send the
// SignalR event. The result itself isn't used on the frontend.
export const loginPingSent = createAction('geoFence/loginPingSent');
// No ping was sent (no location, or the SDK or ping failed), so no event will
// come and the region stays unverified
export const loginPingNotSent = createAction('geoFence/loginPingNotSent');
export const accountModeReceived = createAction<{ mode: boolean }>(
  'geoFence/accountModeReceived',
);
// No change is expected any more, so the stored mode applies
export const accountModeSettled = createAction('geoFence/accountModeSettled');

const initialState: GeoFenceState = {
  accountModeState: 'idle',
};

const geoFence = createSlice({
  name: 'geoFence',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loginPingStarted, (state) => {
      state.accountModeState = 'waiting';
    });
    builder.addCase(loginPingNotSent, (state) => {
      state.accountModeState = 'unverified';
    });
    builder.addCase(accountModeReceived, (state) => {
      state.accountModeState = 'resolved';
    });
    builder.addCase(accountModeSettled, (state) => {
      if (state.accountModeState === 'waiting') {
        state.accountModeState = 'resolved';
      }
    });
    // appLogout only fires when logout succeeds; an expired session ends via
    // refreshToken.rejected and its follow-up logout() usually rejects.
    builder.addMatcher(
      (action) => SESSION_END_ACTION_TYPES.includes(action.type),
      () => initialState,
    );
  },
});

export const withGeoFence = injectSlice({
  reducerPath: 'geoFence',
  reducer: geoFence.reducer,
});

export const selectAccountModeState = withGeoFence.selector(
  (state) => state.geoFence?.accountModeState ?? 'idle',
);

declare global {
  interface LazyLoadedSlices {
    geoFence: ReturnType<typeof geoFence.reducer>;
  }
}
